import { BD_SHARE_PERMISSION_DISPLAY_NAME } from '@bindoc-projects/resource-domain-lib';
import type { BdSharePermission } from '@bindoc-projects/resource-domain-lib';
import { folderItemKey, isSameFolderItem } from '../shareables-overview.models';
import type { FolderItem } from '../shareables-overview.models';

export interface PermissionAvatar {
  permission: BdSharePermission;
  icon: string;
  tooltip: string;
}

const PERMISSION_ICON: Record<BdSharePermission, string> = {
  READ: 'pi pi-eye',
  UPDATE: 'pi pi-pencil',
  DELETE: 'pi pi-trash',
};

const PERMISSION_ORDER: BdSharePermission[] = ['READ', 'UPDATE', 'DELETE'];

/** One offered value of the Typ column filter, with how many files carry it. */
export interface TypeFilterOption {
  readonly label: string;
  readonly count: number;
}

/**
 * The value list of the Typ column filter, derived from the rows themselves.
 *
 * Keyed by {@link FolderItem.typeLabel} — the words in the column — not by the
 * `type` enum: several shareable types share a display name ("Virtuelle
 * Klinik"), and the column sorts by the label too (see
 * {@link compareFolderItemsByField}), so filtering by anything else would offer
 * two indistinguishable entries for one visible value.
 *
 * Folders are excluded: they are navigation and are never filtered away, so a
 * "Ordner" option would be a tick with no effect. `selected` labels that this
 * folder does not contain are kept with `count: 0` — otherwise walking into a
 * sub-folder would strand an active filter value with no way to untick it.
 */
export function typeFilterOptionsFor(
  items: readonly FolderItem[],
  selected: readonly string[] = [],
): TypeFilterOption[] {
  const counts = new Map<string, number>();
  for (const item of items) {
    if (item.isFolder) {
      continue;
    }
    counts.set(item.typeLabel, (counts.get(item.typeLabel) ?? 0) + 1);
  }
  for (const label of selected) {
    if (!counts.has(label)) {
      counts.set(label, 0);
    }
  }
  return [...counts.entries()]
    .map(([label, count]) => ({ label, count }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * Order-insensitive comparison of two Typ-filter value lists. The list box
 * emits its own order, so a plain array compare would report a change on every
 * re-emit — and each reported change drops the row selection.
 */
export function sameTypeFilterValues(
  a: readonly string[],
  b: readonly string[],
): boolean {
  if (a.length !== b.length) {
    return false;
  }
  const other = new Set(b);
  return a.every((label) => other.has(label));
}

export function permissionAvatarsFor(
  permissions: BdSharePermission[] | undefined,
  locale = 'de',
): PermissionAvatar[] {
  if (!permissions || permissions.length === 0) {
    return [];
  }
  return PERMISSION_ORDER.filter((p) => permissions.includes(p)).map((p) => ({
    permission: p,
    icon: PERMISSION_ICON[p],
    tooltip: BD_SHARE_PERMISSION_DISPLAY_NAME[locale]?.[p] ?? p,
  }));
}

/**
 * The subset of {@link permissionAvatarsFor} shown in the compact (narrow
 * layout) meta line: only the rights that go *beyond* seeing the entry. READ
 * is implied — the row would not be listed without it — so rendering an eye on
 * every row costs a glyph and says nothing. What remains is the differentiator:
 * a row with no glyph is read-only, pencil means editable, trash deletable.
 */
export function editingPermissionAvatarsFor(
  permissions: BdSharePermission[] | undefined,
  locale = 'de',
): PermissionAvatar[] {
  return permissionAvatarsFor(permissions, locale).filter(
    (a) => a.permission !== 'READ',
  );
}

export type FolderTableSortBy = 'name' | 'modifiedDate' | 'type';

/**
 * Where the row order comes from.
 *
 * - `fixed` — the columns are not sortable and the table orders the rows itself
 *   along `sortBy` (the Home dashboard list).
 * - `client` — sortable columns, sorted here over the whole list the parent
 *   handed in. The right mode for folder browsing: a folder's children always
 *   arrive complete, so there is nothing to ask a server for.
 * - `parent` — sortable columns, but the parent owns the order and reacts to
 *   `sortChange` (the recent tab, which sorts
 *   server-side across paged requests).
 */
export type FolderTableSortMode = 'fixed' | 'client' | 'parent';

export interface FolderTableSortChange {
  field: FolderTableSortBy;
  order: 1 | -1;
}

/** Milliseconds of a `modifiedDate`; 0 for missing or unparseable values. */
function modifiedTime(value: string | undefined): number {
  if (!value) return 0;
  const t = new Date(value).getTime();
  return Number.isNaN(t) ? 0 : t;
}

/**
 * Ascending comparison of two rows along one sortable column. `type` compares
 * the {@link FolderItem.typeLabel} — the words the user reads — not the raw
 * shareable-type enum value, so the Typ column sorts the way it looks.
 */
export function compareFolderItemsByField(
  a: FolderItem,
  b: FolderItem,
  field: FolderTableSortBy,
): number {
  switch (field) {
    case 'name':
      return a.name.localeCompare(b.name);
    case 'type':
      return a.typeLabel.localeCompare(b.typeLabel);
    case 'modifiedDate':
      return modifiedTime(a.modifiedDate) - modifiedTime(b.modifiedDate);
  }
}

/**
 * The grouping that survives every column sort: live rows before recycle-bin
 * ones, folders before files. Explorer, SharePoint and Drive all keep it — a
 * folder must not get lost between files just because the list is ordered by
 * date. It is also why the table needs no "back to the default order" control:
 * with the grouping fixed, the default order *is* the Name-ascending column
 * state, one click on the Name header away.
 */
function compareFolderItemGroups(a: FolderItem, b: FolderItem): number {
  if (!!a.isRecycleBinItem !== !!b.isRecycleBinItem) {
    return a.isRecycleBinItem ? 1 : -1;
  }
  if (a.isFolder !== b.isFolder) {
    return a.isFolder ? -1 : 1;
  }
  return 0;
}

/**
 * Sort order: the {@link compareFolderItemGroups} grouping first, then
 * `field`/`order` within each group, ties broken by name so equal dates or
 * types still produce a stable, reproducible order.
 */
export function sortFolderItemsBy(
  items: FolderItem[],
  field: FolderTableSortBy,
  order: 1 | -1,
): FolderItem[] {
  return [...items].sort(
    (a, b) =>
      compareFolderItemGroups(a, b) ||
      order * compareFolderItemsByField(a, b, field) ||
      a.name.localeCompare(b.name),
  );
}

/**
 * The default order of a folder listing: non-recycle-bin folders first, then
 * non-recycle-bin files, then recycle-bin items at the bottom; ties broken by
 * `name` lexicographically. Identical to the Name-ascending column sort.
 */
export function sortFolderItems(items: FolderItem[]): FolderItem[] {
  return sortFolderItemsBy(items, 'name', 1);
}

/**
 * Sort order: most-recently modified first; items without a parseable
 * `modifiedDate` sink to the bottom. Robust against both ISO strings and
 * `Date` instances.
 */
export function sortFolderItemsByModifiedDate(
  items: FolderItem[],
): FolderItem[] {
  return [...items].sort(
    (a, b) => modifiedTime(b.modifiedDate) - modifiedTime(a.modifiedDate),
  );
}

/** Keep folders navigable even when the selected types hide their files. */
export function filterFolderItems(
  items: FolderItem[],
  types: readonly string[],
): FolderItem[] {
  const selected = new Set(types);
  return selected.size === 0
    ? items
    : items.filter((item) => item.isFolder || selected.has(item.typeLabel));
}

export function selectableFolderItems(
  items: FolderItem[],
  includeFolders: boolean,
): FolderItem[] {
  return includeFolders ? items : items.filter((item) => !item.isFolder);
}

/** Rebind controlled picks to the current row objects after a reload. */
export function visibleFolderSelection(
  items: FolderItem[],
  selected: readonly FolderItem[],
): FolderItem[] {
  const keys = new Set(selected.map(folderItemKey));
  return items.filter((item) => keys.has(folderItemKey(item)));
}

/** A checkbox update replaces only this listing's picks, preserving other scopes. */
export function mergeFolderSelection(
  listed: readonly FolderItem[],
  controlled: readonly FolderItem[],
  next: readonly FolderItem[],
): FolderItem[] {
  const keys = new Set(listed.map(folderItemKey));
  return [
    ...controlled.filter((item) => !keys.has(folderItemKey(item))),
    ...next,
  ];
}

export function toggleFolderSelection(
  current: readonly FolderItem[],
  item: FolderItem,
): FolderItem[] {
  return current.some((other) => isSameFolderItem(other, item))
    ? current.filter((other) => !isSameFolderItem(other, item))
    : [...current, item];
}
